<template>
  <BModal
    :visible="value"
    hide-footer
    hide-header
    centered
    lazy
    no-close-on-backdrop
    content-class="bracelet-modal"
    @change="$emit('input', $event)"
    @show="start"
    @hidden="stop"
  >
    <div class="text-center">
      <div
        class="state-icon"
        :class="iconClass"
      >
        <div class="state-icon-inner">
          <i
            v-if="pending"
            class="fas fa-spinner fa-spin"
          />
          <i
            v-else-if="state === 'success'"
            class="fa-regular fa-check"
          />
          <i
            v-else-if="state === 'conflict'"
            class="fa-regular fa-triangle-exclamation"
          />
          <i
            v-else-if="state === 'error'"
            class="fa-regular fa-xmark"
          />
          <i
            v-else
            class="fa-regular fa-nfc-signal"
          />
        </div>
      </div>

      <div class="state-title">
        {{ title }}
      </div>
      <div class="state-info">
        {{ info }}
      </div>

      <div class="actions d-flex">
        <template v-if="state === 'conflict'">
          <button
            type="button"
            class="action-btn cancel"
            :disabled="pending"
            @click="close"
          >
            <i class="fa-regular fa-xmark pr-2" />
            {{ $t('general.cancel') }}
          </button>
          <button
            type="button"
            class="action-btn confirm"
            :disabled="pending"
            @click="assign(true)"
          >
            <i class="fa-regular fa-arrow-right-arrow-left pr-2" />
            {{ $t('shop.todayOrder.bracelet.move') }}
          </button>
        </template>
        <template v-else-if="state === 'error'">
          <button
            type="button"
            class="action-btn cancel"
            @click="close"
          >
            <i class="fa-regular fa-xmark pr-2" />
            {{ $t('general.cancel') }}
          </button>
          <button
            type="button"
            class="action-btn confirm"
            @click="start"
          >
            {{ $t('shop.todayOrder.bracelet.retry') }}
          </button>
        </template>
        <button
          v-else-if="state === 'success'"
          type="button"
          class="action-btn cancel"
          @click="close"
        >
          {{ $t('general.close') }}
        </button>
        <button
          v-else
          type="button"
          class="action-btn cancel"
          :disabled="pending"
          @click="close"
        >
          <i class="fa-regular fa-xmark pr-2" />
          {{ $t('general.cancel') }}
        </button>
      </div>
    </div>
  </BModal>
</template>

<script>
import { mapActions } from 'vuex';

// Readers type the tag id like a keyboard, a longer pause means the keys were not from a scan
const scanKeyInterval = 1000;
const successCloseDelay = 2000;

export default {
  props: {
    // Modal visibility, use with v-model
    value: Boolean,
    payerId: String,
  },
  data: () => ({
    state: 'scanning',
    pending: false,
    tag: '',
    buffer: '',
    lastKeyAt: 0,
    conflictNames: [],
  }),
  computed: {
    title() {
      switch (this.state) {
        case 'success': return this.$t('shop.todayOrder.bracelet.assigned');
        case 'conflict': return this.$t('shop.todayOrder.bracelet.conflictTitle');
        case 'error': return this.$t('shop.todayOrder.bracelet.errorTitle');
        default: return this.$t('shop.todayOrder.bracelet.scanTitle');
      }
    },
    info() {
      switch (this.state) {
        case 'success': return this.$t('shop.todayOrder.bracelet.assignedInfo');
        case 'conflict': return this.$t('shop.todayOrder.bracelet.conflictInfo', [this.conflictNames.join(', ')]);
        case 'error': return this.$t('shop.todayOrder.bracelet.errorInfo');
        default: return this.$t('shop.todayOrder.bracelet.scanInfo');
      }
    },
    iconClass() {
      if (this.state === 'success') return 'green';
      if (this.state === 'conflict') return 'orange';
      if (this.state === 'error') return 'red';
      return 'blue';
    },
  },
  methods: {
    ...mapActions('shop', ['assignBracelet']),
    start() {
      this.state = 'scanning';
      this.pending = false;
      this.tag = '';
      this.buffer = '';
      this.conflictNames = [];
      // Capture phase, so the scan does not reach the search of the today's order screen
      window.addEventListener('keydown', this.onKeyDown, true);
    },
    stop() {
      window.removeEventListener('keydown', this.onKeyDown, true);
      clearTimeout(this.closeTimeout);
    },
    close() {
      this.$emit('input', false);
    },
    onKeyDown(e) {
      e.stopImmediatePropagation();
      if (this.state !== 'scanning' || this.pending) return;

      const now = Date.now();
      if (now - this.lastKeyAt > scanKeyInterval) {
        this.buffer = '';
      }
      this.lastKeyAt = now;

      if (e.key === 'Enter') {
        e.preventDefault();
        const tag = this.buffer.trim();
        this.buffer = '';
        if (tag) {
          this.tag = tag;
          this.assign(false);
        }
      } else if (e.key === 'Escape') {
        this.close();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        this.buffer += e.key;
      }
    },
    assign(move) {
      this.pending = true;
      this.assignBracelet({
        params: {
          payerId: this.payerId,
        },
        data: {
          tag: this.tag,
          move,
        },
      })
        .then(({ data }) => {
          this.state = 'success';
          this.$emit('assigned', data);
          this.closeTimeout = setTimeout(this.close, successCloseDelay);
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            this.conflictNames = (error.response.data?.payers || []).map((p) => p.name);
            this.state = 'conflict';
          } else {
            this.state = 'error';
          }
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  beforeDestroy() {
    this.stop();
  },
};
</script>

<style lang="scss" scoped>
::v-deep .bracelet-modal {
  border-radius: 16px;
  border: none;

  .modal-body {
    padding: 32px 30px 30px;
  }
}

.state-icon {
  width: 110px;
  height: 110px;
  margin: 0 auto 24px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .state-icon-inner {
    width: 64px;
    height: 64px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
  }

  @mixin state-colors($color) {
    background-color: rgba($color, 0.06);
    box-shadow: inset 0 0 0 12px rgba($color, 0.06);

    .state-icon-inner {
      background-color: rgba($color, 0.18);
      color: $color;
    }
  }

  &.blue {
    @include state-colors($blue);
  }

  &.green {
    @include state-colors($green);
  }

  &.orange {
    @include state-colors(#f0a000);
  }

  &.red {
    @include state-colors($red);
  }
}

.state-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.state-info {
  color: #555;
  margin-bottom: 24px;
}

.actions {
  gap: 10px;
}

.action-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 22px;
  color: white;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;

  &.cancel {
    background-color: #9a9a9a;
  }

  &.confirm {
    background-color: $blue;
  }

  &:disabled {
    opacity: 0.6;
  }
}
</style>
